/**
 * Client-safe role utilities.
 *
 * SERVER-ONLY helpers (`canManageSchool`, `isPendingMemberFromAuth`,
 * `getActiveSchoolIsAdmin`) live in `@/lib/server/roles-server` — they read
 * `getActiveSchool()` which uses Clerk `auth()` (server-only) and would
 * otherwise leak server-only imports into client bundles via this file's
 * transitive importers.
 *
 * Per-school role information comes from `UsersOnSchools.isAdmin` via
 * /v1/users/me: server callers use `@/lib/server/roles-server`; client callers
 * use `useSchool()` from `@/components/context/school-context`. The chokepoint
 * functions in both files source from those single points so consumers don't
 * have to.
 *
 * Super-admin is still claim-based (top-level `isSuperAdmin` JWT claim).
 *
 * This file uses TYPE-ONLY imports from `@clerk/nextjs/server` (erased at
 * compile time, no runtime server-only leak) so client components can
 * safely import from it.
 */

import type { auth } from '@clerk/nextjs/server'
import type { useAuth } from '@clerk/nextjs'
import { SessionClaims } from '@/types/auth'

export type UserRole = 'superAdmin' | 'schoolAdmin' | 'faculty' | 'student' | 'parent' | 'member'

type ServerAuth = Awaited<ReturnType<typeof auth>>
type ClientAuth = ReturnType<typeof useAuth>

/**
 * Reads the top-level `isSuperAdmin` boolean from a server-side `auth()` result.
 * Pure claim read, no server-only runtime imports — safe to call from server
 * components / route handlers.
 */
export function isSuperAdminFromAuth(authResult: ServerAuth): boolean {
  return (authResult.sessionClaims as SessionClaims | null | undefined)?.isSuperAdmin === true
}

/**
 * Reads the top-level `isSuperAdmin` boolean from the client-side `useAuth()` hook.
 */
export function isSuperAdminFromClient(authResult: ClientAuth): boolean {
  return (authResult.sessionClaims as SessionClaims | null | undefined)?.isSuperAdmin === true
}

/**
 * Client-side equivalent of `isPendingMemberFromAuth`. The status must be
 * passed in from `useSchool().activeSchoolUserStatus` because hooks can't
 * be called from inside a util.
 */
export function isPendingMemberFromClient(_authResult: ClientAuth, schoolUserStatus: string | null): boolean {
  return schoolUserStatus === 'PENDING'
}
